import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { fr } from "./translations/fr";
import { en } from "./translations/en";
import { es } from "./translations/es";
import type { TranslationKeyInput } from "./types";

export type { TranslationKey, TranslationKeyInput, TranslationSchema } from "./types";

export type Language = "fr" | "en" | "es";

const translations: Record<Language, typeof fr> = { fr, en, es };

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: TranslationKeyInput) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "fr",
  setLang: () => {},
  t: (key) => key,
});

function detectLanguage(): Language {
  const stored = localStorage.getItem("lang") as Language | null;
  if (stored && translations[stored]) return stored;
  const browser = navigator.language.slice(0, 2);
  if (browser === "es") return "es";
  if (browser === "en") return "en";
  return "fr";
}

function getNestedValue(obj: any, path: string): string {
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current == null) return "";
    current = current[part];
  }
  return typeof current === "string" ? current : "";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(detectLanguage);

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    localStorage.setItem("lang", l);
    // Force a hard refresh so every component (including memoized text,
    // SEO tags, and lazy chunks) re-reads from the new translation bundle.
    // Prevents the UI from being stuck with empty/stale strings after a
    // language switch.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  const t = useCallback(
    (key: TranslationKeyInput) => getNestedValue(translations[lang], key),
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  return useContext(LanguageContext);
}
